import { z } from 'zod';
import { OtpPurpose, Provider, ProviderResultStatus, ScoringRequestStatus } from '../enums.js';
import { tiyinSchema } from './common.js';
/**
 * Scoring API kontrakti (BT §6.3).
 * Frontend ↔ Backend o'rtasidagi yagona haqiqat manbai.
 */
const providerEnum = z.enum([Provider.ALIF, Provider.UZUM]);
const resultStatusEnum = z.nativeEnum(ProviderResultStatus);
/**
 * POST /api/scoring — parallel scoringni boshlash.
 *
 * BT §6.3.1 da so'rov ichida butun profil, karta va JSHSHIR yuborilishi
 * ko'rsatilgan edi. Bu yerda faqat `clientId` yuboriladi — sabab:
 * mijoz allaqachon MyID orqali yaratilgan va uning ma'lumotlari bazada
 * shifrlangan holda yotibdi. Ularni yana brauzer orqali haydash — shaxsiy
 * ma'lumotni keraksiz joyda ochish demakdir (BT §6.8).
 * Provayderlarga yuboriladigan yukni backend o'zi yig'adi.
 */
export const startScoringRequestSchema = z.object({
    clientId: z.string().uuid(),
    /**
     * Takroriy so'rov dublikat yaratmasligi uchun (BT §6.8 — idempotentlik).
     * Frontend bitta urinish uchun bitta kalit yuboradi: tugma ikki marta
     * bosilsa ham ikkita scoring ketmaydi.
     */
    idempotencyKey: z.string().uuid().optional(),
    /**
     * Kim nomidan yuborilsin (faqat ADMIN uchun).
     *
     * Alifga so'rov sotuvchining identifikatori bilan ketadi va u
     * Alifda ro'yxatdan o'tgan bo'lishi shart. Ma'mur odatda
     * ro'yxatda bo'lmaydi — shuning uchun u yubora oladigan
     * sotuvchini tanlaydi, va ariza ham o'shanikiga yoziladi.
     */
    onBehalfOfUserId: z.string().uuid().optional(),
});
/** Provayder taklif qilgan bitta shart (muddat + oylik to'lov). */
export const providerLimitOptionSchema = z.object({
    /** Provayder tomonidagi shart identifikatori (Alif: condition_id). */
    conditionId: z.string(),
    /** Muddat — oylarda. */
    duration: z.number().int().positive(),
    /** Ushbu muddat uchun maksimal summa, tiyinda. */
    maxAmount: tiyinSchema,
    /** Oylik to'lov, tiyinda (agar provayder bersa). */
    monthlyPayment: tiyinSchema.optional(),
    /** Ustama foizi (agar provayder bersa). */
    markupPercent: z.number().min(0).optional(),
});
/**
 * Provayder scoring o'rtasida so'ragan kod.
 *
 * Bu ariza OTP'sidan (`otpSessionSchema`) boshqa narsa: u shartnoma
 * bosqichida, bu esa scoring hali tugamasidan oldin — provayder kartani
 * tasdiqlamaguncha limitni hisoblamaydi.
 */
export const providerOtpPromptSchema = z.object({
    purpose: z.nativeEnum(OtpPurpose),
    /** Kod qaysi raqamga yuborilgan, maskalangan holda. */
    phoneMask: z.string().nullable(),
    /**
     * Kod qachongacha amal qiladi. Ekranda hisoblagich ko'rsatiladi.
     *
     * Bu BAHO, kafolat emas: kodni qabul qilish yoki qilmaslikni provayder
     * hal qiladi. Shuning uchun vaqt tugagach ham kiritish taqiqlanmaydi —
     * shunchaki yangi kod so'rash taklif qilinadi.
     */
    expiresAt: z.string().datetime(),
    /** Necha marta noto'g'ri kiritilgan. */
    attempts: z.number().int().nonnegative(),
    maxAttempts: z.number().int().positive(),
    /** Kodni yana necha marta qayta yuborish mumkin. */
    resendsLeft: z.number().int().nonnegative(),
    /**
     * FAQAT `mock` rejimda to'ladi. Real rejimda kodni provayder
     * to'g'ridan-to'g'ri mijozga yuboradi va biz uni bilmaymiz.
     */
    mockCode: z.string().optional(),
});
/** Bitta provayderning natijasi. */
export const providerResultSchema = z.object({
    provider: providerEnum,
    status: resultStatusEnum,
    /** Tasdiqlangan maksimal summa, tiyinda. REJECTED/TIMEOUT bo'lsa — null. */
    maxAmount: tiyinSchema.nullable(),
    limits: z.array(providerLimitOptionSchema),
    /** REJECTED bo'lsa to'ldiriladi, so'rov tilida (BT §6.3.2). */
    rejectReason: z.string().nullable(),
    /**
     * Sababning lug'atdagi kaliti — frontend tilni almashtirsa, matnni
     * serverdan qayta so'ramasdan tarjima qiladi. Provayder o'z matnini
     * bergan bo'lsa (real rejim) — `null`.
     */
    rejectReasonKey: z.string().nullable(),
    /** Provayder tomonidagi mijoz identifikatori — keyingi so'rovlar uchun. */
    externalUserId: z.string().nullable(),
    /** `AWAITING_OTP` holatida to'ladi — ekranda kod kiritish formasi chiqadi. */
    otp: providerOtpPromptSchema.nullable(),
    /**
     * Provayder haqiqiy API bilan ishlayaptimi yoki namuna rejimidami.
     *
     * Ekranda ko'rsatiladi: jonli sinov paytida bitta provayder haqiqiy,
     * ikkinchisi namuna bo'lishi mumkin — soxta limitni haqiqiy deb
     * o'ylash xavfli.
     */
    mode: z.enum(['mock', 'real']).default('real'),
    /** So'rov qancha davom etdi, ms — audit va monitoring uchun. */
    durationMs: z.number().int().nonnegative().nullable(),
    updatedAt: z.string().datetime(),
});
/** GET /api/scoring/:id va POST /api/scoring javobi (BT §6.3.2). */
export const scoringSessionSchema = z.object({
    sessionId: z.string().uuid(),
    status: z.nativeEnum(ScoringRequestStatus),
    clientId: z.string().uuid(),
    results: z.object({
        alif: providerResultSchema,
        uzum: providerResultSchema,
    }),
    /** Mijoz tanlagan provayder (agar tanlangan bo'lsa). */
    chosenProvider: providerEnum.nullable(),
    /**
     * Butun scoring qancha davom etdi, ms.
     * BT §9: parallel ishlagani uchun bu qiymat eng sekin provayder vaqtiga
     * yaqin bo'lishi kerak, ikkalasining yig'indisiga emas.
     */
    totalDurationMs: z.number().int().nonnegative().nullable(),
    createdAt: z.string().datetime(),
});
/** POST /api/scoring/:id/choose. */
export const chooseProviderRequestSchema = z.object({
    provider: providerEnum,
});
/** POST /api/scoring/:id/otp — provayder so'ragan kodni uzatish. */
export const confirmScoringOtpRequestSchema = z.object({
    provider: providerEnum,
    code: z.string().regex(/^\d{4,6}$/, 'v.otpCode'),
});
/** POST /api/scoring/:id/otp/resend — kodni qayta yuborishni so'rash. */
export const resendScoringOtpRequestSchema = z.object({
    provider: providerEnum,
});
//# sourceMappingURL=scoring.js.map